import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton, Badge, Popover, Box, Typography, List, ListItemButton, ListItemText,
  Button, Divider, CircularProgress, Tooltip, Stack,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import notificationApi, { type NotificationItem } from '@/api/notification';

/** 根据通知关联类型解析跳转链接 */
function refToPath(item: NotificationItem): string {
  if (item.refType === 'quotation_demand') return '/account/quotation?tab=customer-demands';
  if (item.refType === 'quotation') return '/account/quotation?tab=my-quotations';
  return '/notifications';
}

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await notificationApi.unreadCount();
      setUnread(r.count || 0);
    } catch {
      /* 忽略：通知不可用不应影响主界面 */
    }
  }, []);

  // 进入即拉取未读数，并定时刷新
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  const handleOpen = async (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setLoading(true);
    try {
      const r = await notificationApi.list({ limit: 12, offset: 0 });
      setItems(r.items || []);
      setUnread(r.unreadCount || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (item: NotificationItem) => {
    if (!item.isRead) {
      try {
        await notificationApi.markRead(item.id);
        setUnread((n) => Math.max(0, n - 1));
        setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, isRead: true } : x)));
      } catch {
        /* ignore */
      }
    }
    handleClose();
    navigate(refToPath(item));
  };

  const handleMarkAll = async () => {
    try {
      await notificationApi.markAll();
      setUnread(0);
      setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
    } catch {
      /* ignore */
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  return (
    <>
      <Tooltip title={unread > 0 ? `你有 ${unread} 条未读通知` : '通知'}>
        <IconButton onClick={open ? handleClose : handleOpen} size="small" sx={{ ml: 1 }}>
          <Badge color="error" badgeContent={unread > 99 ? '99+' : unread} max={99}>
            {unread > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: '90vw', mt: 1 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            通知{unread > 0 ? `（${unread} 未读）` : ''}
          </Typography>
          <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAll} disabled={unread === 0}>
            全部已读
          </Button>
        </Stack>
        <Divider />
        <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              暂无通知
            </Typography>
          ) : (
            <List disablePadding>
              {items.map((it) => (
                <ListItemButton key={it.id} onClick={() => handleItemClick(it)} sx={{ alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: it.isRead ? 'transparent' : 'primary.main',
                      mt: 0.8,
                      mr: 1.25,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={it.title}
                    secondary={it.body || ''}
                    primaryTypographyProps={{ fontWeight: it.isRead ? 400 : 700, fontSize: 14, noWrap: false }}
                    secondaryTypographyProps={{ fontSize: 12, sx: { whiteSpace: 'normal' } }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" onClick={handleViewAll}>
            查看全部通知
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
